import React,{useEffect} from 'react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  size?: 'sm'|'md'|'lg'|'xl'|'full';
  children: React.ReactNode;
  actions?: React.ReactNode;   // fixed footer buttons
  icon?: string;
}

const WIDTHS = {
  sm:   'max-w-sm w-full',
  md:   'max-w-lg w-full',
  lg:   'max-w-2xl w-full',
  xl:   'max-w-4xl w-full',
  full: 'w-full',
};

export default function SlidePanel({isOpen,onClose,title,subtitle,size='lg',children,actions,icon}: Props){
  // Lock body scroll when open
  useEffect(()=>{
    if(isOpen) document.body.style.overflow='hidden';
    else document.body.style.overflow='';
    return ()=>{document.body.style.overflow='';};
  },[isOpen]);

  // Close on Escape
  useEffect(()=>{
    if(!isOpen) return;
    const handler=(e:KeyboardEvent)=>{ if(e.key==='Escape') onClose(); };
    window.addEventListener('keydown',handler);
    return ()=>window.removeEventListener('keydown',handler);
  },[isOpen,onClose]);

  if(!isOpen) return null;

  return(
    <div className="fixed inset-0 z-50 flex" style={{isolation:'isolate'}}>
      {/* Backdrop — click to close */}
      <div
        className="flex-1 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Close panel"
      />

      {/* Panel — slides in from right */}
      <div
        className={`${WIDTHS[size]} w-full bg-white h-full flex flex-col shadow-2xl border-l border-slate-200`}
        style={{animation:'slideInRight 0.22s ease-out'}}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b bg-slate-50 flex-shrink-0">
          {icon&&<span className="text-2xl">{icon}</span>}
          <div className="flex-1 min-w-0">
            <h2 className="font-bold text-slate-800 text-base leading-tight truncate">{title}</h2>
            {subtitle&&<p className="text-xs text-slate-400 mt-0.5 truncate">{subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-200 transition-colors text-lg font-bold"
            title="Close (Esc)"
          >✕</button>
        </div>

        {/* Content — scrollable */}
        <div className="flex-1 overflow-y-auto p-5">
          {children}
        </div>

        {/* Footer actions — fixed */}
        {actions&&(
          <div className="flex-shrink-0 px-5 py-3 border-t bg-white">
            {actions}
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
      `}</style>
    </div>
  );
}
